import "../../../components/dashboard/offers/offer-workspace.css";
import React, { useState } from "react";
import { useDispatch, useSelector } from "react-redux";
import { useNavigate } from "react-router-dom";
import { useTranslation } from "react-i18next";
import { Form, Formik } from "formik";
import * as Yup from "yup";
import { toast } from "react-hot-toast";
import Breadcrumbs from "../../../components/dashboard/breadcrumbs";
import { PMField } from "../../../components/utils/PMField";
import { CustomerSelector } from "../../../components/utils/moduleSelectors/customerSelector";
import { ContactsSelector } from "../../../components/utils/moduleSelectors/contactSelector";
import { ProjectSelector } from "../../../components/utils/moduleSelectors/projectSelector";
import { EmployeeSelector } from "../../../components/utils/moduleSelectors/employeeSelector";
import { createOfferAsync } from "../../../slices/offer";
import { mapErrors } from "../../../components/Utils";
import { customerLockBlocksSubmit } from "../../../contexts/CustomerLockTracker";

const START_MODES = ["quick", "template", "detailed"];

/**
 * Create-offer page (/dashboard/offers/new).
 * Collects stamdata then creates via createOfferAsync and navigates to the new offer.
 *
 * startMode only changes the first workspace landing:
 * - quick → tilbudslinjer / kundevisning
 * - template → kalkyle + malvelger
 * - detailed → tom kalkyle
 */
export const NewOffer = () => {
  const { t } = useTranslation();
  const dispatch = useDispatch();
  const navigate = useNavigate();
  const currentUser = useSelector((state) => state.session.currentUser);
  const [startMode, setStartMode] = useState("quick");
  const [customer, setCustomer] = useState(null);
  const [project, setProject] = useState(null);
  const [contact, setContact] = useState(null);

  const selectionChange = (setSelected, formik) => (event) => {
    setSelected(event?.target?.data || null);
    formik.handleChange(event);
  };

  return (
    <div className="min-h-full bg-slate-50">
      <div className="bg-white">
        <Breadcrumbs currentPageTitle={t("offers.create.title")} />
      </div>
      <div className="mx-auto w-full max-w-3xl px-4 py-6 sm:px-6">
        <div className="rounded-2xl border border-slate-200 bg-white p-5 shadow-[0_1px_2px_rgba(0,0,0,.04),0_6px_22px_rgba(0,0,0,.07)] sm:p-7">
          <div className="mb-6">
            <h1 className="text-xl font-semibold text-slate-900">
              {t("offers.create.title")}
            </h1>
            <p className="mt-1 text-sm text-slate-500">
              {t("offers.create.intro")}
            </p>
          </div>

          <section className="mb-2" aria-labelledby="offer-create-start-heading">
            <h2
              id="offer-create-start-heading"
              className="text-sm font-semibold text-slate-900"
            >
              {t("offers.create.start_heading")}
            </h2>
            <p className="mt-1 text-sm text-slate-500">
              {t("offers.create.start_intro")}
            </p>

            <div
              className="offer-start-options"
              role="radiogroup"
              aria-label={t("offers.workspace.start")}
            >
              {START_MODES.map((mode) => {
                const selected = startMode === mode;
                return (
                  <button
                    type="button"
                    key={mode}
                    role="radio"
                    aria-checked={selected}
                    aria-pressed={selected}
                    onClick={() => setStartMode(mode)}
                  >
                    <strong>{t(`offers.workspace.start_${mode}`)}</strong>
                    <span className="offer-start-options__hint">
                      {t(`offers.workspace.start_${mode}_hint`)}
                    </span>
                    <span className="offer-start-options__detail">
                      {t(`offers.workspace.start_${mode}_detail`)}
                    </span>
                  </button>
                );
              })}
            </div>

            <p className="offer-start-selected" role="status">
              <span className="offer-start-selected__label">
                {t("offers.workspace.start_selected_label")}:
              </span>{" "}
              <strong>{t(`offers.workspace.start_${startMode}`)}</strong>
              {" — "}
              {t(`offers.workspace.start_${startMode}_detail`)}
            </p>
          </section>

          <Formik
            initialValues={{
              customer_id: "",
              project_id: "",
              contact_id: "",
              our_contact_employee_id: currentUser?.id || "",
              reference: "",
              comment: "",
              description: "",
            }}
            validationSchema={Yup.object({
              customer_id: Yup.string().required(
                t("offers.create.customer_required")
              ),
              reference: Yup.string().max(255),
              comment: Yup.string().max(5000),
            })}
            onSubmit={async (values, helpers) => {
              try {
                helpers.setSubmitting(true);
                const payload = {
                  ...values,
                  customer_id: values.customer_id || null,
                  project_id: values.project_id || null,
                  contact_id: values.contact_id || null,
                  our_contact_employee_id:
                    values.our_contact_employee_id || null,
                  description: values.comment,
                };
                const result = await dispatch(createOfferAsync(payload));

                if (createOfferAsync.rejected.match(result)) {
                  const error = result.payload;
                  const message =
                    mapErrors(
                      error?.response?.data?.errors || error?.errors
                    ) ||
                    error?.response?.data?.error ||
                    error?.error ||
                    t("offers.create.failed");
                  helpers.setErrors({ submit: message });
                  toast.error(message);
                  return;
                }

                const created = result.payload?.offer || result.payload;
                if (!created?.id) {
                  helpers.setErrors({ submit: t("offers.create.failed") });
                  toast.error(t("offers.create.failed"));
                  return;
                }

                toast.success(t("offers.create.success"));
                navigate(
                  `/dashboard/offers/${created.id}?tab=${
                    startMode === "quick" ? "tilbudslinjer" : "kalkyle"
                  }${startMode === "template" ? "&start=template" : ""}`
                );
              } catch (err) {
                const message = err?.message || t("offers.create.failed");
                helpers.setErrors({ submit: message });
                toast.error(message);
              } finally {
                helpers.setSubmitting(false);
              }
            }}
          >
            {(formik) => (
              <Form className="space-y-5">
                <div className="grid grid-cols-1 gap-5 sm:grid-cols-2">
                  <CustomerSelector
                    label={t("offers.create.customer")}
                    selected={customer}
                    onChange={selectionChange(setCustomer, formik)}
                    {...formik}
                  />
                  <ProjectSelector
                    label={t("offers.create.project")}
                    selected={project}
                    onChange={selectionChange(setProject, formik)}
                    {...formik}
                  />
                  <ContactsSelector
                    label={t("offers.create.contact")}
                    name="contact_id"
                    module_param={customer?.id || formik.values.customer_id}
                    selected={contact}
                    disabled={!formik.values.customer_id}
                    onChange={selectionChange(setContact, formik)}
                    {...formik}
                  />
                  <EmployeeSelector
                    name="our_contact_employee_id"
                    label={t("offers.create.responsible")}
                    selected={currentUser}
                    {...formik}
                  />
                </div>
                <PMField
                  name="reference"
                  type="text"
                  label={t("offers.create.reference")}
                  {...formik}
                />
                <PMField
                  name="comment"
                  type="text"
                  multiline
                  rows={5}
                  label={t("offers.create.description")}
                  {...formik}
                />
                {formik.errors.submit && (
                  <p className="text-sm text-red-600">{formik.errors.submit}</p>
                )}
                <div className="flex justify-end gap-3 border-t border-slate-200 pt-5">
                  <button
                    type="button"
                    onClick={() => navigate("/dashboard/offers")}
                    className="rounded-lg border border-slate-300 bg-white px-4 py-2.5 text-sm font-medium text-slate-700 hover:bg-slate-50"
                  >
                    {t("offers.create.cancel")}
                  </button>
                  <button
                    type="submit"
                    disabled={
                      formik.isSubmitting ||
                      customerLockBlocksSubmit(formik.values)
                    }
                    className="rounded-lg bg-[#1463C7] px-4 py-2.5 text-sm font-semibold text-white shadow-[0_12px_24px_-8px_rgba(20,99,199,.6)] hover:bg-[#0B3A78] disabled:cursor-not-allowed disabled:bg-slate-200 disabled:text-slate-400 disabled:shadow-none"
                  >
                    {formik.isSubmitting
                      ? t("offers.create.creating")
                      : t("offers.create.submit")}
                  </button>
                </div>
              </Form>
            )}
          </Formik>
        </div>
      </div>
    </div>
  );
};
